import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LeadCaptureModal from "./LeadCaptureModal";
import logo from "../assets/new logo.svg";
import { HiMenuAlt3 } from "react-icons/hi";
import { AiOutlineClose } from "react-icons/ai";
import { FiSearch } from "react-icons/fi";
import { config } from "../config";

const Navbar = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const pathActive = (path) => {
    if (path === "/") return location.pathname === "/";
    if (path === "/courses") {
      return location.pathname === "/courses" || location.pathname.startsWith("/courses/");
    }
    return location.pathname === path;
  };

  const isActive = (path) => pathActive(path);

  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setShowMobileMenu(false);
  }, []);

  const openLeadModal = useCallback(() => {
    setShowLeadModal(true);
  }, []);

  const closeLeadModal = useCallback(() => {
    setShowLeadModal(false);
  }, []);

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const q = searchQuery.trim();
      if (q) {
        navigate(`/courses?q=${encodeURIComponent(q)}`);
        setSearchQuery("");
        closeMobileMenu();
      }
    },
    [searchQuery, navigate, closeMobileMenu]
  );

  const phoneHref = `tel:${config.PHONE.replace(/\s/g, "")}`;
  const whatsappHref = `https://wa.me/${config.PHONE.replace(/\D/g, "")}`;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 shadow-lg transition-all duration-300">
        {/* Top utility bar */}
        <div className="bg-primary text-white text-xs sm:text-sm border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center lg:justify-between gap-x-4 gap-y-2 py-2">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 min-w-0">
              <a
                href={`mailto:${config.CONTACT_EMAIL}`}
                className="inline-flex items-center gap-1.5 hover:underline min-w-0"
                title={config.CONTACT_EMAIL}
              >
                <span className="text-sm leading-none shrink-0" aria-hidden="true">
                  {"\uD83D\uDCE7"}
                </span>
                <span className="truncate max-w-[210px] xs:max-w-none">{config.CONTACT_EMAIL}</span>
              </a>
              <span className="hidden sm:inline text-white/40">|</span>
              <a href={phoneHref} className="inline-flex items-center gap-1.5 font-medium whitespace-nowrap hover:underline">
                <span className="text-sm leading-none shrink-0" aria-hidden="true">
                  {"\uD83D\uDCDE"}
                </span>
                <span>{config.PHONE}</span>
              </a>
            </div>
            <div className="flex items-center justify-center lg:justify-end gap-3 sm:gap-4 w-full lg:w-auto min-w-0">
              <form
                onSubmit={handleSearchSubmit}
                className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2 py-1 border border-white/20 flex-1 max-w-[260px] lg:flex-initial lg:max-w-[220px]"
                role="search"
              >
                <button type="submit" className="text-white/90 shrink-0 p-1 rounded-full hover:bg-white/15 transition-colors" aria-label="Search courses">
                  <FiSearch aria-hidden />
                </button>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses…"
                  className="bg-transparent border-0 outline-none text-white placeholder:text-white/60 text-xs sm:text-sm w-full min-w-0"
                  aria-label="Search courses"
                />
              </form>
              <div className="flex items-center gap-4 shrink-0">
                <a
                  href="https://www.linkedin.com/company/bim-bytes-solutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/bimbytesacademy?igsh=M2RuZXkyYjZqZWdl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="Instagram"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                    <path d="M7.75 2C4.686 2 2 4.686 2 7.75v8.5C2 19.314 4.686 22 7.75 22h8.5c3.064 0 5.75-2.686 5.75-5.75v-8.5C22 4.686 19.314 2 16.25 2h-8.5zm0 2h8.5c1.93 0 3.75 1.82 3.75 3.75v8.5c0 1.93-1.82 3.75-3.75 3.75h-8.5C5.82 20 4 18.18 4 16.25v-8.5C4 5.82 5.82 4 7.75 4zm8.75 1.5a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5zM12 7a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6z" />
                  </svg>
                </a>
                <a
                  href="https://x.com/bimbytesacademy?s=21"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="Twitter"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                    <path d="M18.244 2H21l-6.56 7.497L22 22h-6.828l-5.35-7.007L3.78 22H1l7.043-8.05L2 2h6.828l4.89 6.438L18.244 2zm-1.2 18h1.8L7.04 4H5.18l11.864 16z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/share/17YWuZ9nsT/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="Facebook"
                >

                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                    <path d="M22 12a10 10 0 10-11.5 9.9v-7H7v-3h3.5V9.5c0-3.5 2.1-5.4 5.2-5.4 1.5 0 3.1.3 3.1.3v3.4h-1.8c-1.8 0-2.3 1.1-2.3 2.2V12H19l-.6 3h-3.7v7A10 10 0 0022 12z" />
                  </svg>
                </a>

                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="WhatsApp"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                  </svg>
                </a>

                <a
                  href="https://www.youtube.com/@BimBytesAcademy"
                  className="group text-white/80 hover:text-white transition flex items-center justify-center shrink-0 w-5 h-5"
                  aria-label="YouTube"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.6 3.5 12 3.5 12 3.5s-7.6 0-9.4.6A3 3 0 00.5 6.2 31.2 31.2 0 000 12a31.2 31.2 0 00.5 5.8 3 3 0 002.1 2.1c1.8.6 9.4.6 9.4.6s7.6 0 9.4-.6a3 3 0 002.1-2.1A31.2 31.2 0 0024 12a31.2 31.2 0 00-.5-5.8zM9.8 15.5v-7l6.3 3.5-6.3 3.5z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-md border-b border-gray-200">
          <div className="relative w-full max-w-7xl flex items-center justify-between py-2 md:py-3 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <Link to="/">
                <img src={logo} alt="BimBytes Academy Logo" className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto" />
              </Link>
            </div>

            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-7">
              <Link to="/" className={isActive("/") ? "nav-link-active" : "nav-link"}>
                Home
              </Link>
              <Link to="/about" className={isActive("/about") ? "nav-link-active" : "nav-link"}>
                About us
              </Link>
              <Link to="/courses" className={isActive("/courses") ? "nav-link-active" : "nav-link"}>
                Courses
              </Link>
              <Link
                to="/why-choose-us"
                className={isActive("/why-choose-us") ? "nav-link-active" : "nav-link"}
              >
                Why Choose Us
              </Link>
              <Link to="/placement" className={isActive("/placement") ? "nav-link-active" : "nav-link"}>
                Placement
              </Link>
              <Link to="/gallery" className={isActive("/gallery") ? "nav-link-active" : "nav-link"}>
                Gallery
              </Link>
              <Link to="/blog" className={isActive("/blog") ? "nav-link-active" : "nav-link"}>
                Blog
              </Link>
              <Link to="/contact" className={isActive("/contact") ? "nav-link-active" : "nav-link"}>
                Contact
              </Link>
            </nav>

            {/* <div className="hidden lg:flex items-center gap-3">
              <button
                type="button"
                onClick={openLeadModal}
                className="px-5 py-2.5 rounded-xl bg-crimson text-white font-semibold shadow-md hover:bg-crimson/90 transition-colors duration-300"
              >
                Enroll Now
              </button>
            </div> */}

            <button
              className="block lg:hidden p-2 rounded-lg bg-primary/20 backdrop-blur-sm hover:bg-primary/30 transition-all duration-300 ease-smooth"
              onClick={toggleMobileMenu}
              aria-label="Open Menu"
            >
              <HiMenuAlt3 size={24} className="text-primary" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] transition-all duration-500 ease-smooth ${showMobileMenu ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeMobileMenu} />
        <div
          className={`absolute right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-500 ease-smooth ${showMobileMenu ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center">
              <img src={logo} alt="BimBytes Academy Logo" className="h-12 w-auto mr-3" />
              <h2 className="text-lg font-semibold text-textPrimary">Menu</h2>
            </div>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              aria-label="Close Menu"
            >
              <AiOutlineClose size={20} className="text-textSecondary" />
            </button>
          </div>

          <nav className="p-6">
            <ul className="space-y-4">
              <li>
                <Link
                  to="/"
                  className={`${isActive("/") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/courses"
                  className={`${isActive("/courses") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Courses
                </Link>
              </li>
              <li>
                <Link
                  to="/why-choose-us"
                  className={`${isActive("/why-choose-us") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Why Choose Us
                </Link>
              </li>
              <li>
                <Link
                  to="/placement"
                  className={`${isActive("/placement") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Placement
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={`${isActive("/about") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className={`${isActive("/gallery") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Gallery
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className={`${isActive("/blog") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={`${isActive("/contact") ? "text-primary font-semibold" : "text-textSecondary hover:text-primary"} transition-colors duration-300 ease-smooth block py-2`}
                  onClick={closeMobileMenu}
                >
                  Contact
                </Link>
              </li>
            </ul>
            <div className="mt-8 space-y-3">
              <button
                onClick={() => {
                  closeMobileMenu();
                  openLeadModal();
                }}
                className="w-full px-4 py-3 bg-crimson text-white rounded-xl font-semibold shadow-md hover:bg-crimson/90 transition-colors duration-300"
              >
                Enroll Now
              </button>
            </div>
          </nav>
        </div>
      </div>

      <LeadCaptureModal isOpen={showLeadModal} onClose={closeLeadModal} />
    </>
  );
};

export default Navbar;
